"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.isConsolidatedAmountSafe = exports.isValidDATEVCreditorNumber = exports.isExpenseSafeForDATEVExport = exports.isExpenseDATEVCompliant = exports.isExpenseAccountCodeDATEVCompliant = exports.isBuCodeDATEVCompliant = void 0;
const datevConsts_1 = require("../consts/datevConsts");
const models_1 = require("../models");
const expenseSplit_model_1 = require("../models/expense/expenseSplit.model");
const common_1 = require("./common");
const expense_utils_1 = require("./expense.utils");
const isBuCodeDATEVCompliant = (buCode) => {
    if (!buCode) {
        return false;
    }
    if (!datevConsts_1.ALLOWED_DATEV_BU_CODES.includes(Number(buCode))) {
        return false;
    }
    // max 4 numeric digits, no leading zero
    if (!/^[1-9][0-9]{0,3}$/.test(buCode)) {
        return false;
    }
    return true;
};
exports.isBuCodeDATEVCompliant = isBuCodeDATEVCompliant;
const isExpenseAccountCodeDATEVCompliant = (expenseAccountCode) => {
    if (!expenseAccountCode) {
        return false;
    }
    const numeric = (0, common_1.isNumeric)(expenseAccountCode);
    const doesNotHaveDecimalNumbers = Number(expenseAccountCode) % 1 === 0;
    const isAtLeastOne = Number(expenseAccountCode) >= 1;
    const noLeadingZeros = expenseAccountCode.length > 0 && expenseAccountCode.substring(0, 1) !== "0";
    const max8Digits = expenseAccountCode.length < 9;
    const noCommaOrDot = !expenseAccountCode.includes(",") && !expenseAccountCode.includes(".");
    return numeric && isAtLeastOne && noLeadingZeros && max8Digits && doesNotHaveDecimalNumbers && noCommaOrDot;
};
exports.isExpenseAccountCodeDATEVCompliant = isExpenseAccountCodeDATEVCompliant;
const isExpenseDATEVCompliant = (expense, taxes, expenseAccounts, checks = { taxes: true, expenseAccounts: true, amounts: true }) => {
    const isReportingOnly = expense.paymentFlowInformation === models_1.ExpensePaymentFlowInformationEnum.REPORTING_ONLY;
    const isNotReviewedYet = !(0, expense_utils_1.isAtLeastReviewed)(expense.status);
    const usedTaxes = [
        taxes.find(({ _id }) => { var _a; return (_id === null || _id === void 0 ? void 0 : _id.toString()) === ((_a = expense.taxRate) === null || _a === void 0 ? void 0 : _a.toString()); }),
        ...expense.splits.filter((split) => split.taxRate).map((split) => taxes.find(({ _id }) => { var _a; return (_id === null || _id === void 0 ? void 0 : _id.toString()) === ((_a = split.taxRate) === null || _a === void 0 ? void 0 : _a.toString()); })),
    ].filter((tax) => !!tax);
    // BU Code Check is ignored for mileage or perdiem expense
    const hasIncompatibleTaxBuCodes = checks.taxes && !(0, expense_utils_1.isMileageOrPerDiem)(expense) && usedTaxes.some((tax) => (tax === null || tax === void 0 ? void 0 : tax.buCode) && !(0, exports.isBuCodeDATEVCompliant)(tax.buCode));
    const usedExpenseAccounts = [
        expenseAccounts.find(({ _id }) => { var _a; return (_id === null || _id === void 0 ? void 0 : _id.toString()) === ((_a = expense.expenseAccount) === null || _a === void 0 ? void 0 : _a.toString()); }),
        ...expense.splits.filter((split) => split.expenseAccount).map((split) => expenseAccounts.find(({ _id }) => (_id === null || _id === void 0 ? void 0 : _id.toString()) === split.expenseAccount.toString())),
    ].filter((expenseAccount) => !!expenseAccount);
    const hasIncompatibleExpenseAccountCodes = checks.expenseAccounts && usedExpenseAccounts.some((expenseAccount) => !(0, exports.isExpenseAccountCodeDATEVCompliant)(expenseAccount === null || expenseAccount === void 0 ? void 0 : expenseAccount.accountCode));
    const hasZeroGrossAmount = (checks.amounts && expense.totalGrossPrice === 0) || expense.splits.some((split) => split.grossPrice === 0 && split.kind !== expenseSplit_model_1.ItemSplitKindEnum.TIP);
    const isConsolidatedAmountUnsafe = !(0, exports.isExpenseSafeForDATEVExport)(expense);
    const checksResults = { isReportingOnly, isNotReviewedYet, hasIncompatibleTaxBuCodes, hasIncompatibleExpenseAccountCodes, hasZeroGrossAmount, isConsolidatedAmountUnsafe };
    const isCompliant = !(isReportingOnly ||
        isNotReviewedYet ||
        hasIncompatibleTaxBuCodes ||
        hasIncompatibleExpenseAccountCodes ||
        hasZeroGrossAmount ||
        isConsolidatedAmountUnsafe);
    return {
        isCompliant,
        checks: checksResults,
    };
};
exports.isExpenseDATEVCompliant = isExpenseDATEVCompliant;
/**
 * If split is manually adjusted, or its a manual entry from the users becuase its an item-split expense, we have to make sure that the adjustments that the user has made is safe for export.
 * Because if its not manually adjusted and it's a SPLIT expense, then in the DATEV export, there is an automatic adjustment mechanism to correct rounding errors
 */
const isExpenseSafeForDATEVExport = (expense) => (0, exports.isConsolidatedAmountSafe)(expense, expense.splits, { splitManuallyAdjusted: expense.splitManuallyAdjusted, splitType: expense.splitType });
exports.isExpenseSafeForDATEVExport = isExpenseSafeForDATEVExport;
const isValidDATEVCreditorNumber = (value) => {
    if (value) {
        const valueString = String(value);
        if (!(0, common_1.isNumeric)(valueString)) {
            return false;
        }
        if (valueString.length > 0 && valueString.substring(0, 1) === "0") {
            return false;
        }
        if (valueString.length < 5) {
            return false;
        }
        if (valueString.length > 9) {
            return false;
        }
    }
    return true;
};
exports.isValidDATEVCreditorNumber = isValidDATEVCreditorNumber;
/**
 * Consolidated amount is a field in DATEV that should be the expense's gross amount.
 *
 * This function checks the splits if the total gross amount of the splits is equal to the expense gross amount.
 * All calculations are done down to the 2nd decimal.
 *
 * Sometimes there are small rounding errors that might cause the total splits to deviate by 0.01 from the expense gross amount, hence dinero is utilized.
 *
 * Another possibility of unsafe consolidated amount is due to user error that they made themselves on the splits.
 */
const isConsolidatedAmountSafe = (expenseAmount, splits, options) => {
    if ((0, common_1.isNotSet)(expenseAmount.totalGrossPrice)) {
        return false;
    }
    if ((0, common_1.isNotSet)(splits)) {
        return false;
    }
    if (options.splitType === models_1.SplitTypeEnum.SPLIT) {
        // check if percentage have a correct total
        const total = splits.reduce((accu, current) => { var _a; return accu + ((_a = Number(current.percentage)) !== null && _a !== void 0 ? _a : 0); }, 0);
        if (!(0, common_1.toDinero)(total).equalsTo((0, common_1.toDinero)(100))) {
            return false;
        }
        // If split is not manually adjusted, make sure that the amount correlates with percentage correctly. This needs to be checked as the numbers might break this if we fail to handle an "odd" data from OCR
        if (!options.splitManuallyAdjusted) {
            for (const split of splits) {
                if ((0, common_1.isNotSet)(split.percentage)) {
                    return false;
                }
                const amountFromPercentage = (0, common_1.toDinero)(expenseAmount.totalGrossPrice).percentage(split.percentage);
                if (!amountFromPercentage.equalsTo((0, common_1.toDinero)(split.grossPrice))) {
                    return false;
                }
            }
            // Since total may not add up properly due to rounding error and will be handled in the DATEV export, return true.
            return true;
        }
    }
    const target = (0, common_1.toDinero)(expenseAmount.totalGrossPrice);
    let splitTotal = (0, common_1.toDinero)(0);
    for (const split of splits) {
        splitTotal = splitTotal.add((0, common_1.toDinero)(split.grossPrice));
    }
    return target.equalsTo(splitTotal);
};
exports.isConsolidatedAmountSafe = isConsolidatedAmountSafe;
